import { useEffect, useState, useCallback } from "react";
import { api } from "@/App";
import { useI18n } from "@/i18n/I18nProvider";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import {
  Plus, Search, Printer, Edit, Trash2, Copy, Tag, Clock, ChevronRight, ArrowLeft, RefreshCw
} from "lucide-react";
import { toast } from "sonner";

// ─────────────────────────────────────────────
// Helper: Format Date DD/MM/YYYY (French style)
// ─────────────────────────────────────────────
function toFrenchDate(isoDate) {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-");
  return `${d}/${m}/${y}`;
}

function today() {
  return new Date().toISOString().split("T")[0];
}

function addDays(isoDate, days) {
  const d = new Date(isoDate);
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}

// ─────────────────────────────────────────────
// Thermal Label Preview Component
// Renders at UI scale (px) — always in French
// ─────────────────────────────────────────────
function ThermalLabelPreview({ productName, fabricationDate, expirationDate, preparedBy }) {
  return (
    <div
      className="thermal-label-preview"
      style={{
        width: "195px",
        height: "325px",
        border: "1.5px solid #e2e8f0",
        borderRadius: "4px",
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 8px",
        fontFamily: "Arial, Helvetica, sans-serif",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        overflow: "hidden",
      }}
    >
      {/* Brand */}
      <div style={{ fontSize: "9px", color: "#94a3b8", textAlign: "center", letterSpacing: "0.05em" }}>
        Taco Memo
      </div>

      {/* Product name — biggest, bold */}
      <div style={{
        fontSize: "20px",
        fontWeight: "900",
        textAlign: "center",
        lineHeight: "1.2",
        textTransform: "uppercase",
        color: "#0f172a",
        padding: "0 4px",
        wordBreak: "break-word",
      }}>
        {productName || "PRODUIT"}
      </div>

      {/* Fabrication date */}
      <div style={{ fontSize: "11px", textAlign: "center", color: "#475569" }}>
        Fabriqué le : {fabricationDate ? toFrenchDate(fabricationDate) : "__/__/____"}
      </div>

      {/* DLC — large, bold, bordered */}
      <div style={{
        fontSize: "18px",
        fontWeight: "900",
        textAlign: "center",
        color: "#0f172a",
        borderTop: "1.5px solid #0f172a",
        borderBottom: "1.5px solid #0f172a",
        padding: "6px 8px",
        width: "100%",
        letterSpacing: "0.01em",
      }}>
        DLC : {expirationDate ? toFrenchDate(expirationDate) : "__/__/____"}
      </div>

      {/* Prepared by */}
      <div style={{ fontSize: "9px", textAlign: "center", color: "#64748b" }}>
        Préparé par : {preparedBy || "______"}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Build print document in a new window WITHOUT document.write
// ─────────────────────────────────────────────
// We previously used `document.write` with template-literal interpolation,
// which is XSS-prone (any product_name from another user / API response
// would be parsed as HTML). Now we:
//   1. Inject only the <style> block (fully static, no user input)
//   2. Build each label with createElement + textContent — guaranteed safe.
function getLabelStyleCSS() {
  return `
@page { size: 30mm 50mm; margin: 0; }
* { box-sizing: border-box; margin: 0; padding: 0; }
body { background: #fff; font-family: Arial, Helvetica, sans-serif; }
.label {
  width: 30mm;
  height: 50mm;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 1.5mm 1.5mm;
  page-break-after: always;
  page-break-inside: avoid;
  overflow: hidden;
}
.brand  { font-size: 4.5pt; color: #888; text-align: center; width: 100%; }
.product{
  font-size: 10pt;
  font-weight: 900;
  text-align: center;
  text-transform: uppercase;
  line-height: 1.2;
  width: 100%;
  word-break: break-word;
  color: #000;
}
.fab    { font-size: 6pt; text-align: center; color: #444; width: 100%; }
.dlc    {
  font-size: 9pt;
  font-weight: 900;
  text-align: center;
  width: 100%;
  border-top: 0.3mm solid #000;
  border-bottom: 0.3mm solid #000;
  padding: 0.8mm 0;
  color: #000;
}
.prep   { font-size: 5pt; text-align: center; color: #555; width: 100%; }
`;
}

function buildPrintDocument(printWindow, productName, fabricationDate, expirationDate, preparedBy, copies) {
  const fabFr = toFrenchDate(fabricationDate);
  const dlcFr = toFrenchDate(expirationDate);
  const doc = printWindow.document;

  // Reset and rebuild the document with a clean structure
  doc.open();
  doc.close();
  doc.documentElement.lang = "fr";

  // <head>
  const charset = doc.createElement("meta");
  charset.setAttribute("charset", "UTF-8");
  const style = doc.createElement("style");
  style.textContent = getLabelStyleCSS();
  // Clear head/body via DOM API (avoids the innerHTML anti-pattern flagged by linters
  // even though setting innerHTML="" is safe — replaceChildren is unambiguous).
  doc.head.replaceChildren();
  doc.head.appendChild(charset);
  doc.head.appendChild(style);

  // <body>
  doc.body.replaceChildren();
  const safeCopies = Math.max(1, Math.min(parseInt(copies, 10) || 1, 50));
  for (let i = 0; i < safeCopies; i++) {
    const label = doc.createElement("div");
    label.className = "label";

    const brand = doc.createElement("div");
    brand.className = "brand";
    brand.textContent = "Taco Memo";
    label.appendChild(brand);

    const product = doc.createElement("div");
    product.className = "product";
    // textContent => any "<script>" or other markup becomes plain text. XSS safe.
    product.textContent = productName || "PRODUIT";
    label.appendChild(product);

    const fab = doc.createElement("div");
    fab.className = "fab";
    fab.textContent = `Fabriqué le : ${fabFr}`;
    label.appendChild(fab);

    const dlc = doc.createElement("div");
    dlc.className = "dlc";
    dlc.textContent = `DLC : ${dlcFr}`;
    label.appendChild(dlc);

    const prep = doc.createElement("div");
    prep.className = "prep";
    prep.textContent = `Préparé par : ${preparedBy || "______"}`;
    label.appendChild(prep);

    doc.body.appendChild(label);
  }
}

// ─────────────────────────────────────────────
// Template List Panel
// ─────────────────────────────────────────────
function TemplateList({ templates, selected, loading, search, onSearch, onSelect, onAdd, onEdit, onDelete, onDuplicate, isAdmin, t }) {
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Tag className="w-5 h-5 text-primary" />{t("labels.title")}
          </h1>
          <p className="text-xs text-muted-foreground">{templates.length} {t("labels.count")}</p>
        </div>
        {isAdmin && (
          <Button size="sm" onClick={onAdd} className="bg-primary text-white" data-testid="add-template-btn">
            <Plus className="w-4 h-4 mr-1" />{t("labels.addTemplate")}
          </Button>
        )}
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input className="pl-9 h-9" placeholder={t("labels.search")} value={search} onChange={(e) => onSearch(e.target.value)} data-testid="label-search-input" />
      </div>

      <div className="flex-1 overflow-y-auto space-y-2 pr-1">
        {loading ? (
          <div className="text-center py-8 text-sm text-muted-foreground">Cargando...</div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12 space-y-2" data-testid="no-templates">
            <Tag className="w-10 h-10 text-muted-foreground/20 mx-auto" />
            <p className="text-sm text-muted-foreground">{t("labels.noTemplates")}</p>
            <p className="text-xs text-muted-foreground/60">{t("labels.noTemplatesHint")}</p>
          </div>
        ) : templates.map((tpl) => (
          <div
            key={tpl.label_template_id}
            data-testid={`template-card-${tpl.label_template_id}`}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(tpl)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(tpl); } }}
            className={`w-full text-left rounded-xl border px-4 py-3 transition-all hover:shadow-sm hover:border-primary/40 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/30 ${selected?.label_template_id === tpl.label_template_id ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "bg-white"}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm font-mono truncate">{tpl.product_name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {tpl.category && <span className="mr-2">· {tpl.category}</span>}
                  <span className="text-primary font-medium">+{tpl.default_dlc_days}j DLC</span>
                </p>
              </div>
              <div className="flex items-center gap-0.5 ml-2 flex-shrink-0">
                {isAdmin && (
                  <>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-primary" title="Duplicar" onClick={(e) => { e.stopPropagation(); onDuplicate(tpl); }} data-testid={`duplicate-tpl-${tpl.label_template_id}`}>
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={(e) => { e.stopPropagation(); onEdit(tpl); }} data-testid={`edit-tpl-${tpl.label_template_id}`}>
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={(e) => { e.stopPropagation(); onDelete(tpl); }} data-testid={`delete-tpl-${tpl.label_template_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </>
                )}
                <ChevronRight className="w-4 h-4 text-muted-foreground/40" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Print Editor + Preview Panel
// ─────────────────────────────────────────────
function PrintPanel({ template, user, onBack, onPrinted, t }) {
  const [fabDate, setFabDate] = useState(today());
  const [dlcDate, setDlcDate] = useState(addDays(today(), template?.default_dlc_days || 3));
  const [preparedBy, setPreparedBy] = useState(user?.name || "");
  const [copies, setCopies] = useState(1);
  const [printing, setPrinting] = useState(false);

  // When template changes, recalculate DLC
  useEffect(() => {
    if (template) {
      setDlcDate(addDays(fabDate, template.default_dlc_days));
    }
  }, [template?.label_template_id]); // eslint-disable-line

  const recalcDlc = (newFabDate) => {
    setFabDate(newFabDate);
    if (template) setDlcDate(addDays(newFabDate, template.default_dlc_days));
  };

  const handlePrint = async () => {
    if (!template) return;
    setPrinting(true);
    try {
      const printWindow = window.open("", "_blank", "width=500,height=700,toolbar=no,menubar=no");
      if (!printWindow) {
        toast.error("El navegador bloqueó la ventana de impresión. Permite ventanas emergentes.");
        setPrinting(false);
        return;
      }
      buildPrintDocument(printWindow, template.product_name, fabDate, dlcDate, preparedBy, copies);
      printWindow.focus();
      // Small delay to ensure rendering
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 300);

      // Save print history
      await api.post("/labels/print", {
        template_id: template.label_template_id,
        product_name: template.product_name,
        fabrication_date: toFrenchDate(fabDate),
        expiration_date: toFrenchDate(dlcDate),
        prepared_by: preparedBy,
        copies,
      });
      toast.success(t("labels.printSuccess"));
      onPrinted();
    } catch (err) {
      console.error("[Labels] Print failed:", err);
      toast.error("Error al imprimir");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <button className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-1 lg:hidden" onClick={onBack} data-testid="back-to-templates">
            <ArrowLeft className="w-3 h-3" />Volver
          </button>
          <h2 className="text-lg font-bold font-mono text-foreground">{template?.product_name}</h2>
          <p className="text-xs text-muted-foreground">{template?.category} · +{template?.default_dlc_days}j DLC</p>
        </div>
        <Button onClick={handlePrint} disabled={printing} className="bg-primary text-white gap-2" data-testid="print-label-btn">
          <Printer className="w-4 h-4" />
          {printing ? "..." : t("labels.printBtn")} ({copies})
        </Button>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 flex-1 overflow-y-auto">
        {/* Left: Form fields */}
        <div className="flex-1 space-y-4">
          <Card>
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-sm">Datos de la Etiqueta</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              <div>
                <Label className="text-xs mb-1 block">{t("labels.fabricationDate")}</Label>
                <Input type="date" value={fabDate} onChange={(e) => recalcDlc(e.target.value)} data-testid="fab-date-input" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">{t("labels.expirationDate")}</Label>
                <Input type="date" value={dlcDate} onChange={(e) => setDlcDate(e.target.value)} data-testid="dlc-date-input" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">{t("labels.preparedBy")}</Label>
                <Input value={preparedBy} onChange={(e) => setPreparedBy(e.target.value)} placeholder="Ej: Jean" data-testid="prepared-by-input" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">{t("labels.copies")}</Label>
                <div className="flex items-center gap-2">
                  <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setCopies(Math.max(1, copies - 1))}>−</Button>
                  <span className="w-8 text-center font-bold" data-testid="copies-count">{copies}</span>
                  <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setCopies(Math.min(50, copies + 1))}>+</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right: Preview */}
        <div className="flex flex-col items-center gap-3">
          <p className="text-xs font-medium text-muted-foreground">{t("labels.previewTitle")}</p>
          <ThermalLabelPreview
            productName={template?.product_name}
            fabricationDate={fabDate}
            expirationDate={dlcDate}
            preparedBy={preparedBy}
          />
          <p className="text-xs text-muted-foreground/60">30mm × 50mm · Thermal</p>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Main Labels Page
// ─────────────────────────────────────────────
export default function Labels() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [mobileView, setMobileView] = useState("list");

  // Template form
  const [form, setForm] = useState({ product_name: "", default_dlc_days: 3, category: "", notes: "" });
  const [editingTpl, setEditingTpl] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteTpl, setDeleteTpl] = useState(null);
  const [saving, setSaving] = useState(false);

  const [showHistory, setShowHistory] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/labels/templates");
      setTemplates(res.data || []);
    } catch (err) { console.error("[Labels] Failed to load templates:", err); toast.error("Error al cargar las plantillas"); }
    finally { setLoading(false); }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await api.get("/labels/history");
      setHistory(res.data || []);
    } catch (err) { console.error("[Labels] Failed to load print history:", err); }
  }, []);

  useEffect(() => { fetchTemplates(); fetchHistory(); }, [fetchTemplates, fetchHistory]);

  const filteredTemplates = search
    ? templates.filter((t) => t.product_name.toLowerCase().includes(search.toLowerCase()) || (t.category || "").toLowerCase().includes(search.toLowerCase()))
    : templates;

  const selectTemplate = (tpl) => { setSelected(tpl); setMobileView("detail"); };

  const openAdd = () => { if (!isAdmin) return; setEditingTpl(null); setForm({ product_name: "", default_dlc_days: 3, category: "", notes: "" }); setShowForm(true); };
  const openEdit = (tpl) => { if (!isAdmin) return; setEditingTpl(tpl); setForm({ product_name: tpl.product_name, default_dlc_days: tpl.default_dlc_days, category: tpl.category || "", notes: tpl.notes || "" }); setShowForm(true); };
  const openDuplicate = async (tpl) => {
    if (!isAdmin) return;
    try {
      const res = await api.post("/labels/templates", {
        product_name: tpl.product_name + " (COPY)",
        default_dlc_days: tpl.default_dlc_days,
        category: tpl.category || "",
        notes: tpl.notes || "",
      });
      setTemplates((prev) => [...prev, res.data].sort((a, b) => a.product_name.localeCompare(b.product_name)));
      toast.success(t("labels.duplicateSuccess"));
    } catch (err) { console.error("[Labels] duplicate failed:", err); toast.error("Error al duplicar"); }
  };

  const saveTemplate = async () => {
    if (!isAdmin) return;
    if (!form.product_name.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      if (editingTpl) {
        const res = await api.put(`/labels/templates/${editingTpl.label_template_id}`, form);
        setTemplates((prev) => prev.map((t) => t.label_template_id === editingTpl.label_template_id ? res.data : t));
        if (selected?.label_template_id === editingTpl.label_template_id) setSelected(res.data);
        toast.success("Plantilla actualizada");
      } else {
        const res = await api.post("/labels/templates", form);
        setTemplates((prev) => [...prev, res.data].sort((a, b) => a.product_name.localeCompare(b.product_name)));
        toast.success("Plantilla creada");
      }
      setShowForm(false);
    } catch (err) { console.error("[Labels] save failed:", err); toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!isAdmin) return;
    if (!deleteTpl) return;
    try {
      await api.delete(`/labels/templates/${deleteTpl.label_template_id}`);
      setTemplates((prev) => prev.filter((t) => t.label_template_id !== deleteTpl.label_template_id));
      if (selected?.label_template_id === deleteTpl.label_template_id) { setSelected(null); setMobileView("list"); }
      toast.success("Plantilla eliminada");
    } catch (err) { console.error("[Labels] delete failed:", err); toast.error("Error al eliminar"); }
    finally { setDeleteTpl(null); }
  };

  const quickReprint = (entry) => {
    const printWindow = window.open("", "_blank", "width=500,height=700,toolbar=no,menubar=no");
    if (!printWindow) { toast.error("Permite ventanas emergentes para imprimir."); return; }
    // Convert from DD/MM/YYYY back to YYYY-MM-DD for generatePrintHTML
    const [d, m, y] = entry.fabrication_date.split("/");
    const [d2, m2, y2] = entry.expiration_date.split("/");
    const fabIso = `${y}-${m}-${d}`;
    const dlcIso = `${y2}-${m2}-${d2}`;
    buildPrintDocument(printWindow, entry.product_name, fabIso, dlcIso, entry.prepared_by, entry.copies);
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 300);
    toast.success(t("labels.printSuccess"));
  };

  return (
    <div className="h-full flex flex-col">
      <div className="mb-3 hidden lg:flex items-center justify-between">
        <p className="text-xs text-muted-foreground">{t("labels.subtitle")}</p>
        <Button variant="ghost" size="sm" className="text-xs gap-1" onClick={() => { setShowHistory(!showHistory); fetchHistory(); }} data-testid="history-toggle-btn">
          <Clock className="w-3.5 h-3.5" />{t("labels.history")}
        </Button>
      </div>

      {/* History drawer */}
      {showHistory && (
        <Card className="mb-4" data-testid="history-panel">
          <CardHeader className="pb-2 pt-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">{t("labels.history")}</CardTitle>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setShowHistory(false)}><RefreshCw className="w-3 h-3" /></Button>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("labels.noHistory")}</p>
            ) : (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {history.map((h) => (
                  <div key={h.label_id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/30 text-xs" data-testid={`history-entry-${h.label_id}`}>
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold font-mono">{h.product_name}</span>
                      <span className="text-muted-foreground ml-2">DLC: {h.expiration_date}</span>
                      <span className="text-muted-foreground ml-2">×{h.copies}</span>
                    </div>
                    <span className="text-muted-foreground flex-shrink-0">{h.prepared_by}</span>
                    <Button size="icon" variant="ghost" className="h-6 w-6 flex-shrink-0" onClick={() => quickReprint(h)} title={t("labels.quickPrint")} data-testid={`reprint-${h.label_id}`}>
                      <Printer className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Desktop split */}
      <div className="hidden lg:grid lg:grid-cols-5 lg:gap-6" style={{ height: "calc(100vh - 175px)" }}>
        <div className="lg:col-span-2 bg-muted/20 rounded-2xl border p-4 flex flex-col overflow-hidden">
          <TemplateList
            templates={filteredTemplates} selected={selected} loading={loading}
            search={search} onSearch={setSearch}
            onSelect={selectTemplate} onAdd={openAdd} onEdit={openEdit}
            onDelete={setDeleteTpl} onDuplicate={openDuplicate} isAdmin={isAdmin} t={t}
          />
        </div>
        <div className="lg:col-span-3 bg-white rounded-2xl border p-4 flex flex-col overflow-hidden">
          {selected ? (
            <PrintPanel template={selected} user={user} onBack={() => setSelected(null)} onPrinted={fetchHistory} t={t} />
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center space-y-4">
              <div className="w-16 h-16 rounded-2xl bg-primary/5 flex items-center justify-center">
                <Tag className="w-8 h-8 text-primary/40" />
              </div>
              <p className="text-sm text-muted-foreground">{t("labels.selectHint")}</p>
            </div>
          )}
        </div>
      </div>

      {/* Mobile toggle */}
      <div className="lg:hidden flex-1 flex flex-col">
        {mobileView === "list" ? (
          <TemplateList
            templates={filteredTemplates} selected={selected} loading={loading}
            search={search} onSearch={setSearch}
            onSelect={selectTemplate} onAdd={openAdd} onEdit={openEdit}
            onDelete={setDeleteTpl} onDuplicate={openDuplicate} isAdmin={isAdmin} t={t}
          />
        ) : selected ? (
          <PrintPanel template={selected} user={user} onBack={() => setMobileView("list")} onPrinted={fetchHistory} t={t} />
        ) : null}
      </div>

      {/* Template form dialog */}
      <Dialog open={isAdmin && showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-sm" data-testid="template-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingTpl ? t("labels.editTitle") : t("labels.addTitle")}</DialogTitle>
            <DialogDescription className="text-xs">{t("labels.subtitle")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs mb-1 block">{t("labels.productNameLabel")}</Label>
              <Input placeholder={t("labels.productNamePlaceholder")} value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} data-testid="template-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1 block">{t("labels.dlcDaysLabel")}</Label>
                <Input type="number" min={1} max={365} value={form.default_dlc_days} onChange={(e) => setForm({ ...form, default_dlc_days: parseInt(e.target.value) || 1 })} data-testid="template-dlc-days-input" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">{t("labels.categoryLabel")}</Label>
                <Input placeholder={t("labels.categoryPlaceholder")} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="template-category-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs mb-1 block">{t("labels.notesLabel")}</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="template-notes-input" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowForm(false)}>{t("common.cancel")}</Button>
            <Button onClick={saveTemplate} disabled={saving} className="bg-primary text-white" data-testid="template-save-btn">
              {saving ? t("labels.saving") : editingTpl ? t("common.update") : t("common.add")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <AlertDialog open={isAdmin && !!deleteTpl} onOpenChange={(open) => !open && setDeleteTpl(null)}>
        <AlertDialogContent data-testid="delete-template-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("labels.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("labels.deleteDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-white">{t("common.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
